import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { 
  MapPin, 
  Phone, 
  Clock, 
  Mail, 
  Navigation, 
  ShieldCheck, 
  Sparkles, 
  Car, 
  Award, 
  Save, 
  Eye, 
  CheckCircle2, 
  MessageCircle,
  ExternalLink,
  Layers
} from 'lucide-react';

export default function AdminStoreLocator() {
  const { storeSettings, updateStoreSettings, addToast } = useShop();

  const [form, setForm] = useState({
    storeName: storeSettings?.storeName || 'M.L. Jewellers & Kitchenware',
    storeAddress: storeSettings?.storeAddress || storeSettings?.address || 'Main Market Road, Near Clock Tower, Jaipur, Rajasthan - 302001',
    storeTimings: storeSettings?.storeTimings || 'Mon - Sun: 10:30 AM to 08:30 PM (All 7 Days Open)',
    storePhone: storeSettings?.storePhone || storeSettings?.phone || '+91 98100 23456',
    storeEmail: storeSettings?.storeEmail || storeSettings?.email || 'contact@mljewellers.com',
    storeLocatorBadge: storeSettings?.storeLocatorBadge || 'Visit Our Flagship Vault Showroom',
    storeLocatorTitle: storeSettings?.storeLocatorTitle || 'Store Location & Showroom Experience',
    storeLocatorSubtitle: storeSettings?.storeLocatorSubtitle || 'Step into our regal showroom to view hand-crafted bridal sets, get certified Karatmeter testing, and trade bullion at live market rates.',
    storeLocatorEnabled: storeSettings?.storeLocatorEnabled !== false,
    storeGoogleMapsUrl: storeSettings?.storeGoogleMapsUrl || storeSettings?.googleMapsUrl || 'https://maps.google.com/?q=Main+Market+Road+Near+Clock+Tower+Jaipur+Rajasthan+302001',
    storeGoogleMapsEmbed: storeSettings?.storeGoogleMapsEmbed || '',
    storeAmenity1: storeSettings?.storeAmenity1 || 'BIS Karatmeter Testing',
    storeAmenity2: storeSettings?.storeAmenity2 || 'Valet Parking Available',
    storeAmenity3: storeSettings?.storeAmenity3 || 'Private Bridal Lounge',
    storeAmenity4: storeSettings?.storeAmenity4 || 'Zero-Loss Gold Exchange',
    storeVipVisitText: storeSettings?.storeVipVisitText || 'Book VIP Visit'
  });

  const [isSaved, setIsSaved] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    updateStoreSettings({
      ...storeSettings,
      ...form,
      // Keep address and phone aligned across the app
      address: form.storeAddress,
      phone: form.storePhone,
      email: form.storeEmail
    });
    setIsSaved(true);
    addToast('Showroom & Store Location details updated successfully!', 'success');
    setTimeout(() => setIsSaved(false), 3000);
  };

  return (
    <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.8rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <MapPin size={14} /> Showroom & Storefront Experience
          </div>
          <h2 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Showroom Location & <span className="text-gold-gradient">Store Locator Manager</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
            Customize Flagship boutique address, visiting hours, concierge phone, email desk, and amenities.
          </p>
        </div>

        <button
          onClick={handleSave}
          className="btn btn-gold"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.6rem',
            fontSize: '0.9rem',
            fontWeight: 800
          }}
        >
          {isSaved ? <CheckCircle2 size={16} /> : <Save size={16} />}
          <span>{isSaved ? 'Changes Saved!' : 'Save Showroom Details'}</span>
        </button>
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.8rem' }}>
        
        {/* Visibility Toggle */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.2rem 1.6rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h4 style={{ margin: '0 0 0.2rem', color: '#fff', fontSize: '0.95rem', fontWeight: 700 }}>
              Showroom Section Status on Homepage
            </h4>
            <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Enable or disable the Store Location & Experience card for your online customers.
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="storeLocatorEnabled"
              checked={form.storeLocatorEnabled}
              onChange={handleChange}
              style={{ width: '18px', height: '18px', accentColor: 'var(--gold-400)' }}
            />
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: form.storeLocatorEnabled ? '#34d399' : '#f87171' }}>
              {form.storeLocatorEnabled ? 'Active (Visible on Homepage)' : 'Hidden (Disabled)'}
            </span>
          </label>
        </div>

        {/* Form Inputs Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.5rem'
        }}>
          
          {/* Card 1: Showroom Identity & Address */}
          <div style={{
            background: '#13131e',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: '1.6rem'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MapPin size={18} color="var(--gold-400)" /> Showroom Identity & Address
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Showroom / Boutique Name *
                </label>
                <input
                  type="text"
                  name="storeName"
                  value={form.storeName}
                  onChange={handleChange}
                  required
                  placeholder="e.g. M.L. Jewellers & Kitchenware"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Flagship Physical Address *
                </label>
                <textarea
                  name="storeAddress"
                  value={form.storeAddress}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="Street, Landmark, City, State - PIN Code"
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Google Maps Directions Link *
                </label>
                <input
                  type="url"
                  name="storeGoogleMapsUrl"
                  value={form.storeGoogleMapsUrl}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/?q=..."
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Google Maps Embed URL (Optional Iframe Src)
                </label>
                <input
                  type="text"
                  name="storeGoogleMapsEmbed"
                  value={form.storeGoogleMapsEmbed}
                  onChange={handleChange}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {/* Card 2: Contact & Visiting Hours */}
          <div style={{
            background: '#13131e',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: '1.6rem'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={18} color="var(--gold-400)" /> Showroom Timings & Direct Desk
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Showroom Timings *
                </label>
                <input
                  type="text"
                  name="storeTimings"
                  value={form.storeTimings}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Mon - Sun: 10:30 AM to 08:30 PM (All 7 Days Open)"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Direct Concierge Phone *
                </label>
                <input
                  type="text"
                  name="storePhone"
                  value={form.storePhone}
                  onChange={handleChange}
                  required
                  placeholder="+91 98100 23456"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Email Desk *
                </label>
                <input
                  type="email"
                  name="storeEmail"
                  value={form.storeEmail}
                  onChange={handleChange}
                  required
                  placeholder="contact@mljewellers.com"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  WhatsApp VIP Visit Button Text
                </label>
                <input
                  type="text"
                  name="storeVipVisitText"
                  value={form.storeVipVisitText}
                  onChange={handleChange}
                  placeholder="e.g. Book VIP Visit"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {/* Card 3: Section Text & 4 Amenities */}
          <div style={{
            background: '#13131e',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: '1.6rem',
            gridColumn: '1 / -1'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Sparkles size={18} color="var(--gold-400)" /> Section Heading & Showroom Amenities (4 Badges)
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem', marginBottom: '1.2rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Top Badge Text
                </label>
                <input
                  type="text"
                  name="storeLocatorBadge"
                  value={form.storeLocatorBadge}
                  onChange={handleChange}
                  placeholder="e.g. Visit Our Flagship Vault Showroom"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Main Section Heading
                </label>
                <input
                  type="text"
                  name="storeLocatorTitle"
                  value={form.storeLocatorTitle}
                  onChange={handleChange}
                  placeholder="e.g. Store Location & Showroom Experience"
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Section Subheading Description
                </label>
                <input
                  type="text"
                  name="storeLocatorSubtitle"
                  value={form.storeLocatorSubtitle}
                  onChange={handleChange}
                  placeholder="Step into our regal showroom to view hand-crafted bridal sets, get certified Karatmeter testing..."
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* 4 Amenities */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.74rem', fontWeight: 700, color: '#10b981', marginBottom: '0.35rem' }}>
                  <ShieldCheck size={14} /> Amenity 1
                </label>
                <input
                  type="text"
                  name="storeAmenity1"
                  value={form.storeAmenity1}
                  onChange={handleChange}
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.74rem', fontWeight: 700, color: '#3b82f6', marginBottom: '0.35rem' }}>
                  <Car size={14} /> Amenity 2
                </label>
                <input
                  type="text"
                  name="storeAmenity2"
                  value={form.storeAmenity2}
                  onChange={handleChange}
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.74rem', fontWeight: 700, color: '#f59e0b', marginBottom: '0.35rem' }}>
                  <Sparkles size={14} /> Amenity 3
                </label>
                <input
                  type="text"
                  name="storeAmenity3"
                  value={form.storeAmenity3}
                  onChange={handleChange}
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.74rem', fontWeight: 700, color: '#8b5cf6', marginBottom: '0.35rem' }}>
                  <Award size={14} /> Amenity 4
                </label>
                <input
                  type="text"
                  name="storeAmenity4"
                  value={form.storeAmenity4}
                  onChange={handleChange}
                  className="input-luxury"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

          </div>

        </div>

        {/* ========================================================================= */}
        {/* LIVE REAL-TIME PREVIEW CARD (MATCHES USER SCREENSHOT) */}
        {/* ========================================================================= */}
        <div style={{
          marginTop: '1.5rem',
          background: '#0a0a12',
          border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-md)',
          padding: '1.8rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem', color: 'var(--gold-400)' }}>
            <Eye size={18} />
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>
              Live Storefront Preview (Exact Match to Customer View)
            </h3>
          </div>

          <div style={{
            background: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            color: '#0f172a'
          }}>
            {/* Left Column Preview */}
            <div style={{ padding: '2rem 1.8rem' }}>
              <h3 style={{
                margin: '0 0 1rem',
                fontSize: '1.25rem',
                fontWeight: 800,
                color: '#0f172a',
                fontFamily: "'Playfair Display', serif, Georgia"
              }}>
                {form.storeName}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', fontSize: '0.86rem', color: '#334155' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <MapPin size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Flagship Address:</strong>
                    <span>{form.storeAddress}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Clock size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Showroom Timings:</strong>
                    <span>{form.storeTimings}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Phone size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Direct Concierge:</strong>
                    <span style={{ color: '#2563eb', fontWeight: 700 }}>{form.storePhone}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Mail size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Email Desk:</strong>
                    <span>{form.storeEmail}</span>
                  </div>
                </div>
              </div>

              {/* Showroom Amenities Badges */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.6rem',
                padding: '1rem 0',
                borderTop: '1px solid #f1f5f9',
                borderBottom: '1px solid #f1f5f9',
                marginBottom: '1.5rem',
                fontSize: '0.76rem',
                color: '#475569'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <ShieldCheck size={15} color="#16a34a" />
                  <span>{form.storeAmenity1}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Car size={15} color="#2563eb" />
                  <span>{form.storeAmenity2}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Sparkles size={15} color="#d97706" />
                  <span>{form.storeAmenity3}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Award size={15} color="#7c3aed" />
                  <span>{form.storeAmenity4}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.7rem 1.25rem',
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  <Navigation size={15} />
                  <span>Get Directions (Google Maps)</span>
                </button>

                <button
                  type="button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    background: '#25D366',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.7rem 1.15rem',
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  <MessageCircle size={15} />
                  <span>{form.storeVipVisitText}</span>
                </button>
              </div>
            </div>

            {/* Right Column Preview: Dark Map Card */}
            <div style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              position: 'relative',
              minHeight: '280px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              color: '#ffffff',
              textAlign: 'center'
            }}>
              <div style={{ position: 'relative', zIndex: 2, maxWidth: '340px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(37, 99, 235, 0.25)',
                  border: '2px solid #3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.2rem',
                  boxShadow: '0 0 24px rgba(59, 130, 246, 0.5)'
                }}>
                  <MapPin size={28} color="#60a5fa" />
                </div>

                <p style={{ margin: '0 0 1.2rem', fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.4 }}>
                  {form.storeAddress}
                </p>

                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    padding: '0.6rem 1.2rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  <Navigation size={13} />
                  <span>Open in Google Maps</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Save Action Bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
          <button
            type="submit"
            className="btn btn-gold"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.8rem 2rem',
              fontSize: '0.95rem',
              fontWeight: 800
            }}
          >
            <Save size={17} />
            <span>Save Showroom Details</span>
          </button>
        </div>

      </form>
    </div>
  );
}
